import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PenTool, Check, X, RotateCw, MousePointer2, FileText, ArrowRight, ShieldCheck, Edit3, Trash2 } from 'lucide-react';
import { API_BASE } from '../apiConfig';

const styles = {
  editorWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
    height: '100%',
    position: 'relative',
    animation: 'fadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) both',
  },
  topToolsBar: {
    background: '#fff',
    borderRadius: '16px',
    padding: '8px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '24px', // Gap between tool groups
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    border: '1px solid #f1f5f9',
  },
  toolGroup: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '4px',
    gap: '4px'
  },
  toolGroupDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    paddingRight: '24px',
    borderRight: '1px solid #e2e8f0'
  },
  toolItem: (active) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    background: active ? '#faeaea' : 'transparent',
    color: active ? '#ef4444' : '#0f172a',
    transition: 'all 0.15s ease',
  }),
  toolIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toolLabel: {
    fontSize: '12px',
    fontWeight: 600,
  },
  statusBadge: {
    marginLeft: '12px',
    fontSize: '11px',
    fontWeight: 700,
    padding: '4px 12px',
    borderRadius: '999px',
    background: '#f1f5f9',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  formattingBar: {
    background: '#fff',
    borderRadius: '8px',
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 2px 4px -1px rgba(0,0,0,0.05)',
    border: '1px solid #f1f5f9',
    width: 'fit-content',
    margin: '-12px auto 0 auto', // Pull up slightly towards top toolbar
  },
  formatGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    paddingRight: '12px',
    marginLeft: '4px',
    borderRight: '1px solid #e2e8f0',
  },
  formatBtn: (active) => ({
    background: active ? '#e2e8f0' : 'transparent',
    border: 'none',
    borderRadius: '4px',
    height: '28px',
    minWidth: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontFamily: 'serif',
    fontSize: '15px',
    color: '#1e293b',
    transition: 'all 0.1s',
    opacity: 1,
  }),
  formatSelect: {
    background: '#f8fafc',
    border: 'none',
    borderRadius: '4px',
    height: '28px',
    padding: '0 8px',
    fontSize: '13px',
    color: '#1e293b',
    cursor: 'pointer',
    outline: 'none',
  },
  canvasSection: {
    flex: 1,
    borderRadius: '24px',
    padding: '40px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    overflow: 'auto',
    background: '#cbd5e1', 
  },
  pageContainer: {
    position: 'relative',
    background: '#fff',
    boxShadow: '0 0 50px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease',
    cursor: 'crosshair',
  },
  page: {
    width: '595px',
    height: '842px',
    background: '#fff',
    padding: 0,
    position: 'relative',
    overflow: 'hidden',
    textAlign: 'left', // Ensure no unintended centering
  },
  annotation: {
    position: 'absolute',
    color: '#16a34a',
    fontSize: '15px',
    fontWeight: 500,
    pointerEvents: 'none',
  },
  hitZone: (isEdited) => ({
    position: 'absolute',
    background: isEdited ? '#fff' : 'rgba(22, 163, 74, 0.15)',
    border: isEdited ? '1px solid #065f46' : '1px solid rgba(22, 163, 74, 0.3)',
    cursor: 'text',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s',
  }),
  hitInput: {
    width: '100%',
    height: 'auto',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    padding: '0 1px',
    lineHeight: 'normal',
    fontFamily: 'sans-serif',
    display: 'inline-block',
  },
  floatingToolbar: {
    background: '#fff',
    padding: '12px 24px',
    borderRadius: '999px',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    border: '1px solid #f1f5f9',
    zIndex: 100,
  },
  paginationBox: {
    background: '#fff',
    padding: '8px 16px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    border: '1px solid #e2e8f0',
    zIndex: 100,
  },
  bottomBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '0 24px',
    background: 'transparent',
  },
  toolbarBtn: {
    fontSize: '18px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#64748b',
  },
  zoomText: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#1e293b',
    minWidth: '50px',
    textAlign: 'center',
  },
  finishBtn: {
    background: '#065f46',
    color: '#fff',
    padding: '10px 24px',
    borderRadius: '999px',
    fontSize: '14px',
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 6px -1px rgba(6, 95, 70, 0.4)',
  },
  exportOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255,255,255,0.7)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
    borderRadius: '24px',
    animation: 'fadeIn 0.3s ease',
  },
  exportPanel: {
    background: '#fff',
    padding: '32px',
    borderRadius: '24px',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '320px',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.4)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '60px',
    zIndex: 1000,
    backdropFilter: 'blur(12px)',
  },
  modalContent: {
    background: '#fff',
    borderRadius: '20px',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
    border: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    padding: '16px 24px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#0f172a',
    flex: 1,
  },
  modalTabs: {
    display: 'flex',
    background: '#f8fafc',
    margin: '16px 24px',
    padding: '4px',
    borderRadius: '12px',
    gap: '4px',
    border: '1px solid #edf2f7',
  },
  modalTab: (active) => ({
    flex: 1,
    padding: '10px',
    borderRadius: '8px',
    border: 'none',
    background: active ? '#fff' : 'transparent',
    color: active ? '#dc2626' : '#718096',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: active ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  }),
  modalFooter: {
    padding: '20px 24px',
    background: '#f8fafc',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    borderTop: '1px solid #edf2f7',
  },
  btn: (primary) => ({
    padding: '10px 24px',
    borderRadius: '10px',
    border: primary ? 'none' : '1px solid #e2e8f0',
    background: primary ? '#dc2626' : '#fff',
    color: primary ? '#fff' : '#4a5568',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  }),
  signatureOverlay: (isSelected) => ({
    position: 'absolute',
    border: isSelected ? '2px dashed #059669' : '1px dashed #cbd5e1',
    background: isSelected ? 'rgba(5, 150, 105, 0.05)' : 'transparent',
    cursor: 'move',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: isSelected ? 100 : 50,
    borderRadius: '4px',
  }),
  signSubToolbar: {
    display: 'flex',
    background: '#fff',
    padding: '6px',
    borderRadius: '14px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    gap: '6px',
    alignItems: 'center',
    marginTop: '12px',
  },
  signSubBtn: (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '10px',
    border: active ? '1.5px solid #059669' : '1.5px solid #e2e8f0',
    background: active ? '#f0fdf4' : '#fff',
    color: active ? '#059669' : '#1e293b',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  }),
  divider: {
    width: '1px',
    height: '24px',
    background: '#e2e8f0',
    margin: '0 4px',
  },
};

const TOOLS = [
  { id: 'view', name: 'View', svg: (active) => <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4l7.07 17 2.51-7.39L21 11l-17-7z"/><path d="M11 2l1.5 2"/><path d="M15 4l-1.5 2"/></svg> },
  { id: 'text', name: 'Edit PDF', svg: (active) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg> },
  { id: 'sign', name: 'Sign', svg: (active) => <Edit3 width="22" height="22" /> },
];

export default function PDFEditor({ file, onFinish }) {
  const [activeTool, setActiveTool] = useState('view');
  
  const toggleTool = (id) => {
    setActiveTool(prev => prev === id ? 'view' : id);
  };

  const [zoom, setZoom] = useState(110);

  const [isFinished, setIsFinished] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingText, setLoadingText] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [textSpans, setTextSpans] = useState([]);
  const [pageDimensions, setPageDimensions] = useState([595, 842]);
  const [annotations, setAnnotations] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [activeSpanIndex, setActiveSpanIndex] = useState(null);
  const [hasSelection, setHasSelection] = useState(false);
  const savedSelectionRef = useRef(null);
  const activeEditRef = useRef(null); // always points to the focused contenteditable span
  const pageRef = useRef(null);
  const innerPageRef = useRef(null);
  const canvasRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const isToolbarInteractingRef = useRef(false);
  const [activeAnnotationId, setActiveAnnotationId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Sign Tool State
  const [showSignModal, setShowSignModal] = useState(false);
  const [activeSignature, setActiveSignature] = useState(null);
  const [activeSignatureDims, setActiveSignatureDims] = useState({ w: 180, h: 80 });
  const [savedSignatures, setSavedSignatures] = useState([]);
  const [isPlacingSign, setIsPlacingSign] = useState(false);
  const [activeSignSubTool, setActiveSignSubTool] = useState('select'); // 'select', 'add-sig', 'check', 'cross', 'circle'
  const [mousePos, setMousePos] = useState(null);

  // Undo/Redo State
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const isInternalUpdateRef = useRef(false);

  // Sync annotations to history whenever they change
  useEffect(() => {
    if (isInternalUpdateRef.current) {
        isInternalUpdateRef.current = false;
        return;
    }

    // Do not record history WHILE dragging, wait until drag ends
    if (draggingId !== null) {
        return;
    }

    // Deep comparison to avoid unnecessary history states
    const currentAnnotJson = JSON.stringify(annotations);
    const lastHistoryJson = JSON.stringify(history[historyIndex]);

    if (currentAnnotJson !== lastHistoryJson) {
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push([...annotations]);
        
        // Cap history to 50 items
        if (newHistory.length > 50) newHistory.shift();
        
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    }
  }, [annotations, history, historyIndex, draggingId]);

  const undo = () => {
    if (historyIndex > 0) {
      isInternalUpdateRef.current = true;
      const prevIndex = historyIndex - 1;
      setHistoryIndex(prevIndex);
      setAnnotations([...history[prevIndex]]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      isInternalUpdateRef.current = true;
      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setAnnotations([...history[nextIndex]]);
    }
  };

  useEffect(() => {
    if (file) {
      fetchPageImage(file, currentPage);
      fetchTextSpans(file, currentPage);
    }
  }, [file, currentPage]);

  const fetchPageImage = async (f, pageIdx) => {
    setLoadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', f);
      formData.append('page', pageIdx);
      const res = await fetch(`${API_BASE}/render-pdf`, {
        method: 'POST',
        body: formData,
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      }
    } catch (err) {
      console.error("Failed to render PDF page:", err);
    } finally {
      setLoadingImage(false);
    }
  };

  const fetchTextSpans = async (f, pageIdx) => {
    setLoadingText(true);
    try {
      const formData = new FormData();
      formData.append('file', f);
      formData.append('page', pageIdx);
      // Adding explicit headers for multipart if needed, though browser usually handles it
      const res = await fetch(`${API_BASE}/convert/inspect`, {
        method: 'POST',
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setTextSpans(data.spans || []);
        if (data.dimensions) setPageDimensions(data.dimensions);
        if (data.totalPages) setTotalPages(data.totalPages);
      } else {
        console.error("Inspect response not OK:", res.status);
      }
    } catch (err) {
      console.error("Text inspection failed:", err);
    } finally {
      setLoadingText(false);
    }
  };

  const handleSpanEdit = (index, newText, richHtml = undefined) => {
    console.log(`[DEBUG] handleSpanEdit: index=${index}, text="${newText}", richHtml=${!!richHtml}`);
    setAnnotations(prev => {
      const span = textSpans[index];
      const existingAnnotIndex = prev.findIndex(a => a.spanIndex === index && a.page === currentPage);
      const existingAnnot = existingAnnotIndex >= 0 ? prev[existingAnnotIndex] : {};
      
      const newAnnot = {
        ...existingAnnot, // Preserve ALL existing properties including richHtml
        type: 'replace-text',
        spanIndex: index,
        page: currentPage,
        content: newText.trim(),
        bbox: span.bbox,
        size: existingAnnot.size || span.size,
        font: existingAnnot.font || span.font,
        color: existingAnnot.color || span.color,
        isBold: existingAnnot.isBold !== undefined ? existingAnnot.isBold : span.isBold,
        isItalic: existingAnnot.isItalic !== undefined ? existingAnnot.isItalic : span.isItalic,
        textAlign: existingAnnot.textAlign || span.textAlign || 'left',
        origin: span.origin
      };

      if (richHtml !== undefined) {
        newAnnot.richHtml = richHtml;
      }

      if (existingAnnotIndex >= 0) {
        const updated = [...prev];
        updated[existingAnnotIndex] = newAnnot;
        return updated;
      } else {
        return [...prev, newAnnot];
      }
    });
  };

  const updateActiveSpan = (updates) => {
    console.log(`[DEBUG] updateActiveSpan:`, updates);
    if (activeSpanIndex === null) return;
    
    setAnnotations(prev => {
      const existingAnnotIndex = prev.findIndex(a => a.spanIndex === activeSpanIndex && a.page === currentPage);
      const span = textSpans[activeSpanIndex];
      
      let newAnnot;
      if (existingAnnotIndex >= 0) {
        newAnnot = { ...prev[existingAnnotIndex], ...updates };
      } else {
        newAnnot = {
          type: 'replace-text',
          spanIndex: activeSpanIndex,
          page: currentPage,
          content: span.text,
          bbox: span.bbox,
          size: span.size,
          font: span.font,
          color: span.color,
          isBold: span.isBold,
          isItalic: span.isItalic,
          origin: span.origin,
          ...updates
        };
      }
      
      if (existingAnnotIndex >= 0) {
        const updated = [...prev];
        updated[existingAnnotIndex] = newAnnot;
        return updated;
      } else {
        return [...prev, newAnnot];
      }
    });
  };

  const getFormat = () => {
    if (activeSpanIndex === null) return { font: 'Arial', size: 11, isBold: false, isItalic: false, color: '#000000' };
    const annot = annotations.find(a => a.spanIndex === activeSpanIndex && a.page === currentPage);
    if (annot) return annot;
    const span = textSpans[activeSpanIndex];
    return span || { font: 'Arial', size: 11, isBold: false, isItalic: false, color: '#000000' };
  };

  const currentFormat = (() => {
    if (activeAnnotationId !== null) {
      const annot = annotations.find(a => a.id === activeAnnotationId);
      if (annot) {
        return {
          isBold: annot.isBold || false,
          isItalic: annot.isItalic || false,
          color: annot.color || '#000000',
          size: annot.size || 12,
          font: annot.font || 'Arial'
        };
      }
    }
    return getFormat();
  })();

  // ----- Formatting toolbar logic -----
  const saveSelection = (e) => {
    const sel = window.getSelection();
    
    // Always update the ref if we have a non-collapsed selection inside our active editor
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      if (activeEditRef.current && activeEditRef.current.contains(sel.anchorNode)) {
        savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
        setHasSelection(true);
        console.log("[DEBUG] Selection Secured:", savedSelectionRef.current.toString());
      }
    }
    // Only clear if it's a legitimate collapsed cursor move, not a toolbar interaction
    else if (sel && sel.isCollapsed && e && (e.type === 'mouseup' || e.type === 'keyup')) {
       // If the user just clicked (collapsed), we are no longer in selection mode
       setHasSelection(false);
    }
  };

  const applyFormat = (command, value = null) => {
    // If we are editing a manual annotation (Sign tool)
    if (activeAnnotationId !== null) {
        setAnnotations(prev => prev.map(a => {
            if (a.id === activeAnnotationId) {
                const updates = {};
                if (command === 'foreColor') updates.color = value;
                if (command === 'fontName') updates.font = value;
                if (command === 'fontSize') updates.size = value;
                if (command === 'bold') updates.isBold = !a.isBold;
                if (command === 'italic') updates.isItalic = !a.isItalic;
                return { ...a, ...updates };
            }
            return a;
        }));
        return;
    }

    const editEl = activeEditRef.current;
    if (!editEl) return;

    const sel = window.getSelection();
    let range = null;

    // 1. Prioritize LIVE browser selection if it's non-collapsed and inside our element
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed && editEl.contains(sel.anchorNode)) {
        range = sel.getRangeAt(0);
    } 
    // 2. Fallback to our RESTORED selection ONLY if focus was lost to the toolbar
    else if (hasSelection && savedSelectionRef.current && !savedSelectionRef.current.collapsed) {
        range = savedSelectionRef.current;
    }

    const isSelectionActive = !!range;
    console.log(`[DEBUG] applyFormat: ${command} = ${value}, isSelectionActive: ${isSelectionActive}`);
    
    if (isSelectionActive) {
      editEl.focus();
      const s = window.getSelection();
      s.removeAllRanges();
      s.addRange(range);
    }

    if (editEl && isSelectionActive) {
      // Execute the formatting command for SELECTION
      console.log("[DEBUG] Applying formatting to selection");
      
      if (command === 'fontSize') {
        document.execCommand('fontSize', false, '7');
        const targets = editEl.querySelectorAll('font[size="7"], font[size="+4"], span[style*="xx-large"]');
        targets.forEach(t => {
          t.removeAttribute('size');
          t.style.fontSize = `${value}px`;
        });
      } else {
        document.execCommand(command, false, value);
      }

      // IMPORTANT: We do NOT call updateActiveSpan here for selection-based changes.
      // Calling it triggers a React re-render which wipes the current DOM selection and range.
      // The DOM change from execCommand is already visible; persistence is handled by onBlur.
    } else {
      // Apply to ENTIRE span annotation
      console.log("[DEBUG] Applying formatting to whole span");
      // If we are applying a global style, we should clear richHtml 
      // otherwise old inline styles will override the new span-level styles.
      const resetRich = { richHtml: null };
      
      if (command === 'bold')              updateActiveSpan({ isBold: !fmt.isBold, ...resetRich });
      else if (command === 'italic')       updateActiveSpan({ isItalic: !fmt.isItalic, ...resetRich });
      else if (command === 'underline')    updateActiveSpan({ isUnderline: !fmt.isUnderline, ...resetRich });
      else if (command === 'strikeThrough') updateActiveSpan({ isStrikethrough: !fmt.isStrikethrough, ...resetRich });
      else if (command === 'foreColor')    updateActiveSpan({ color: value, ...resetRich });
      else if (command === 'fontSize')     updateActiveSpan({ size: value, ...resetRich });
      else if (command === 'fontName')     updateActiveSpan({ font: value, ...resetRich });
      
      // Re-apply focus
      if (editEl) setTimeout(() => editEl.focus(), 10);
    }
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleDirectDownload = async () => {
    setIsExporting(true);
    try {
      const formData = new FormData();
      formData.append('files', file); // Backend expects 'files' (plural)
      formData.append('options', JSON.stringify([`Edits: ${JSON.stringify(annotations)}`]));
      
      const response = await fetch(`${API_BASE}/convert/pdf-edit`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) throw new Error("Conversion failed");
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name.replace(/\.[^.]+$/, '') + '_edited.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Failed to create PDF");
    } finally {
      setIsExporting(false);
    }
  };

  const handlePageClick = (e) => {
    if (activeTool === 'sign' && innerPageRef.current) {
      const rect = innerPageRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / (zoom / 100);
      const y = (e.clientY - rect.top) / (zoom / 100);
      const id = Date.now();

      if (activeSignature && activeSignSubTool !== 'select') {
          const newAnnot = {
            id,
            type: 'image-sign',
            image: activeSignature,
            x: x - activeSignatureDims.w / 2,
            y: y - activeSignatureDims.h / 2,
            w: activeSignatureDims.w,
            h: activeSignatureDims.h,
            page: currentPage,
            rotation: 0
          };
          setAnnotations(prev => [...prev, newAnnot]);
          setActiveAnnotationId(id);
          setActiveSignature(null); // Clear active signature after placement
          setActiveSignSubTool('select');
          return;
      }
    }

    if (isFinished) return;
    
    // Check if we clicked on an existing span hitzone - if so, don't add new text
    if (e.target.closest('.hit-zone')) return;
  };

  const handleDragUpdate = (e) => {
    if (draggingId === null) return;
    const rect = pageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - dragOffset.x) / (zoom / 100);
    const y = (e.clientY - rect.top - dragOffset.y) / (zoom / 100);
    
    setAnnotations(prev => prev.map(a => a.id === draggingId ? { ...a, x, y } : a));
  };

  const handleDragEnd = () => {
    setDraggingId(null);
  };

  return (
    <div style={styles.editorWrapper}>
      {!isFinished && (
        <div style={styles.topToolsBar}>
          <div style={styles.toolGroupDivider}>
            {/* Undo / Redo */}
            <div 
              style={{ ...styles.toolItem(false), opacity: historyIndex > 0 ? 1 : 0.4, cursor: historyIndex > 0 ? 'pointer' : 'not-allowed' }}
              onClick={undo}
            >
               <span style={styles.toolIcon}>
                 <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
               </span>
               <span style={styles.toolLabel}>Undo</span>
            </div>
            <div 
              style={{ ...styles.toolItem(false), opacity: historyIndex < history.length - 1 ? 1 : 0.3, cursor: historyIndex < history.length - 1 ? 'pointer' : 'not-allowed' }}
              onClick={redo}
            >
               <span style={styles.toolIcon}>
                 <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3l3 2.7"/></svg>
               </span>
               <span style={{...styles.toolLabel, color: historyIndex < history.length - 1 ? '#475569' : '#94a3b8'}}>Redo</span>
            </div>
          </div>
          
          <div style={styles.toolGroup}>
            {TOOLS.map(tool => (
              <div 
                key={tool.id} 
                style={styles.toolItem(activeTool === tool.id)}
                onClick={() => {
                  if (tool.id === 'sign') setShowSignModal(true);
                  setActiveTool(tool.id);
                }}
              >
                <span style={{ ...styles.toolIcon, color: activeTool === tool.id ? '#dc2626' : '#64748b' }}>
                  {tool.id === 'sign' ? <Edit3 size={22} /> : tool.svg(activeTool === tool.id)}
                </span>
                <span style={styles.toolLabel}>{tool.name}</span>
              </div>
            ))}
          </div>
          
          {(loadingText || loadingImage) && (
            <div style={styles.statusBadge}>
              <span>🌀</span> Preparing Document...
            </div>
          )}
        </div>
      )}

      {!isFinished && activeTool === 'sign' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: '12px' }}>
            <div style={styles.signSubToolbar}>
                <button 
                  style={styles.signSubBtn(activeSignSubTool === 'select')}
                  onClick={() => { setActiveSignSubTool('select'); setActiveSignature(null); }}
                >
                    <MousePointer2 size={18} /> Select
                </button>
                <button 
                  style={styles.signSubBtn(activeSignSubTool === 'add-sig')}
                  onClick={() => setShowSignModal(true)}
                >
                    <PenTool size={18} /> Add Signature
                </button>
                <div style={styles.divider} />
                <button 
                  style={styles.signSubBtn(activeSignSubTool === 'check')}
                  onClick={() => {
                    const canvas = document.createElement('canvas');
                    canvas.width = 400; canvas.height = 400;
                    const ctx = canvas.getContext('2d');
                    ctx.strokeStyle = '#000000'; ctx.lineWidth = 24; ctx.lineCap = 'round';
                    ctx.beginPath(); ctx.moveTo(80, 200); ctx.lineTo(180, 300); ctx.lineTo(340, 100); ctx.stroke();
                    setActiveSignature(canvas.toDataURL('image/png'));
                    setActiveSignatureDims({ w: 40, h: 40 });
                    setActiveSignSubTool('check');
                  }}
                >
                    <Check size={18} /> Check
                </button>
                <button 
                  style={styles.signSubBtn(activeSignSubTool === 'cross')}
                  onClick={() => {
                    const canvas = document.createElement('canvas');
                    canvas.width = 400; canvas.height = 400;
                    const ctx = canvas.getContext('2d');
                    ctx.strokeStyle = '#000000'; ctx.lineWidth = 24; ctx.lineCap = 'round';
                    ctx.beginPath(); ctx.moveTo(100, 100); ctx.lineTo(300, 300); ctx.moveTo(300, 100); ctx.lineTo(100, 300); ctx.stroke();
                    setActiveSignature(canvas.toDataURL('image/png'));
                    setActiveSignatureDims({ w: 40, h: 40 });
                    setActiveSignSubTool('cross');
                  }}
                >
                    <X size={18} /> Cross
                </button>
                <button 
                  style={styles.signSubBtn(activeSignSubTool === 'circle')}
                  onClick={() => {
                    const canvas = document.createElement('canvas');
                    canvas.width = 400; canvas.height = 400;
                    const ctx = canvas.getContext('2d');
                    ctx.strokeStyle = '#000000'; ctx.lineWidth = 24; ctx.beginPath(); ctx.arc(200, 200, 140, 0, Math.PI * 2); ctx.stroke();
                    setActiveSignature(canvas.toDataURL('image/png'));
                    setActiveSignatureDims({ w: 40, h: 40 });
                    setActiveSignSubTool('circle');
                  }}
                >
                    <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid currentColor' }} /> Circle
                </button>
            </div>

            {savedSignatures.length > 0 && (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', background: '#fff', padding: '12px 20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                    <span style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sign Saved:</span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {savedSignatures.map((sig, i) => (
                            <div 
                              key={i} 
                              style={{ width: '80px', height: '40px', border: activeSignature === sig ? '2px solid #059669' : '1.5px solid #e2e8f0', borderRadius: '8px', background: activeSignature === sig ? '#f0fdf4' : '#fff', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              onClick={() => { setActiveSignature(sig); setActiveSignatureDims({ w: 180, h: 80 }); setActiveSignSubTool('add-sig'); }}
                            >
                                <img src={sig} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} alt="saved" />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
      )}

      {!isFinished && activeTool !== 'sign' && (activeTool === 'text' || activeTool === 'add-text' || activeAnnotationId !== null) && (
        <div 

          id="formatting-toolbar"
          style={styles.formattingBar} 
          onMouseDown={(e) => {
            isToolbarInteractingRef.current = true;
            // Reset after a delay to catch the following events (like click/change)
            setTimeout(() => { isToolbarInteractingRef.current = false; }, 1000);

            // Aggressively prevent focus loss from the editor
            if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'SELECT' && e.target.tagName !== 'OPTION') {
              e.preventDefault();
            }
          }}
        >
          <div style={styles.formatGroup}>
            <select 
              style={{ ...styles.formatSelect, width: '100px' }}
              value={currentFormat.font ? currentFormat.font.replace(/-[a-zA-Z]+$/, '') : 'Arial'}
              onChange={(e) => applyFormat('fontName', e.target.value)}
              disabled={activeSpanIndex === null && activeAnnotationId === null}
            >
              <option value="Arial">Arial</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Courier">Courier</option>
              <option value="Caveat">Caveat (Signature)</option>
              <option value="Sacramento">Sacramento (Fancy)</option>
              <option value="Satisfy">Satisfy (Scriptly)</option>
            </select>
            
            <select 
              style={{ ...styles.formatSelect, width: '100px' }} 
              disabled={activeSpanIndex === null && activeAnnotationId === null}
            >
              <option>Normal text</option>
              <option>Title</option>
              <option>Subtitle</option>
            </select>
            
            <select 
              style={{ ...styles.formatSelect, width: '60px' }}
              value={Math.round(currentFormat.size) || 11}
              onChange={(e) => applyFormat('fontSize', parseInt(e.target.value))}
              disabled={activeSpanIndex === null && activeAnnotationId === null}
            >
              <option value="9">9</option><option value="10">10</option><option value="11">11</option>
              <option value="12">12</option><option value="14">14</option><option value="16">16</option>
              <option value="18">18</option><option value="20">20</option><option value="24">24</option>
            </select>
          </div>

          <div style={styles.formatGroup}>
            <button 
              style={{ ...styles.formatBtn(currentFormat.isBold), fontWeight: 'bold' }}
              onMouseDown={(e) => { e.preventDefault(); saveSelection(); }}
              onClick={() => applyFormat('bold')}
              disabled={activeSpanIndex === null && activeAnnotationId === null}
            >B</button>
            <button 
              style={{ ...styles.formatBtn(currentFormat.isItalic), fontStyle: 'italic' }}
              onMouseDown={(e) => { e.preventDefault(); saveSelection(); }}
              onClick={() => applyFormat('italic')}
              disabled={activeSpanIndex === null && activeAnnotationId === null}
            >I</button>
            <button 
              style={{ ...styles.formatBtn(currentFormat.isUnderline), textDecoration: 'underline' }}
              onMouseDown={(e) => { e.preventDefault(); saveSelection(); }}
              onClick={() => applyFormat('underline')}
              disabled={activeSpanIndex === null && activeAnnotationId === null}
            >U</button>
          </div>

          <div style={styles.formatGroup}>
            <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', background: '#f8fafc', padding: '0 8px', borderRadius: '4px', height: '28px', color: '#1e293b' }}>
             <button 
              style={{ ...styles.formatBtn(false), position: 'relative', display: 'flex', flexDirection: 'column', gap: '0' }}
              onMouseDown={(e) => {
                e.preventDefault();
                saveSelection(e);
              }}
              onClick={(e) => {
                const input = e.currentTarget.querySelector('input');
                if (input) input.click();
              }}
            >
              <span style={{ fontSize: '15px', fontWeight: 800 }}>A</span>
              <div style={{ width: '16px', height: '3px', background: currentFormat.color || '#000', borderRadius: '1px' }} />
              <input 
                type="color" 
                value={currentFormat.color || '#000000'} 
                onChange={(e) => {
                   applyFormat('foreColor', e.target.value);
                }}
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                disabled={activeSpanIndex === null && activeAnnotationId === null}
              />
            </button>
            </div>
          </div>


        </div>
      )}

      <div style={styles.canvasSection}>
        <div 
          ref={pageRef}
          onMouseMove={(e) => {
            handleDragUpdate(e);
            if (activeTool === 'sign' && (activeSignature || activeSignSubTool !== 'select') && innerPageRef.current) {
              const rect = innerPageRef.current.getBoundingClientRect();
              setMousePos({
                x: (e.clientX - rect.left) / (zoom / 100),
                y: (e.clientY - rect.top) / (zoom / 100)
              });
            }
          }}
          onMouseUp={handleDragEnd}
          onMouseLeave={() => {
            handleDragEnd();
            setMousePos(null);
          }}
          onClick={handlePageClick}
          style={{ 
            ...styles.pageContainer, 
            transform: `scale(${zoom / 100})`,
            transformOrigin: 'top center',
            position: 'relative', 
            width: '100%', 
            height: 'auto',
            display: 'flex',
            justifyContent: 'center',
            cursor: activeTool === 'add-text' ? 'crosshair' : 'default'
          }}
        >
         <div ref={innerPageRef} style={{ ...styles.page, width: pageDimensions[0], height: pageDimensions[1], flexShrink: 0, position: 'relative' }}>
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="PDF Page"
              draggable={false}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: `${pageDimensions[0]}px`,
                height: `${pageDimensions[1]}px`,
                objectFit: 'fill', // Force-fit to the points box
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />
          ) : (
            <div style={{ padding: '40px', background: '#f8fafc', height: '100%' }}>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>Loading PDF...</div>
            </div>
          )}

          {/* Ghost Preview */}
          {activeTool === 'sign' && mousePos && (
             <div style={{
               position: 'absolute',
               left: mousePos.x,
               top: mousePos.y,
               pointerEvents: 'none',
               zIndex: 300,
               opacity: 0.5,
               transform: 'translate(-50%, -50%)'
             }}>
               {activeSignature && (
                 <img 
                   src={activeSignature} 
                   style={{ 
                     width: `${activeSignatureDims.w}px`, 
                     height: `${activeSignatureDims.h}px`, 
                     objectFit: 'contain' 
                   }} 
                   alt="ghost" 
                 />
               )}
             </div>
          )}

          {annotations.filter(a => a.page === currentPage).map(ann => {
            if (ann.type === 'image-sign') {
              return (
                <DraggableSignature 
                  key={ann.id}
                  sig={ann}
                  dims={{ w: pageDimensions[0], h: pageDimensions[1] }}
                  zoom={zoom}
                  innerPageRef={innerPageRef}
                  onUpdate={(updates) => {
                    setAnnotations(prev => prev.map(a => a.id === ann.id ? { ...a, ...updates } : a));
                  }}
                  onRemove={(id) => {
                    setAnnotations(prev => prev.filter(a => a.id !== id));
                  }}
                  isSelected={activeAnnotationId === ann.id}
                  onSelect={() => setActiveAnnotationId(ann.id)}
                />
              );
            }
            if (ann.type === 'replace-text') {
              if (activeTool === 'text' && !isFinished) return null;
              return (
                <div 
                  key={`repl-${ann.spanIndex}`} 
                  style={{
                    position: 'absolute',
                    left: ann.bbox[0],
                    top: ann.bbox[1],
                    minWidth: ann.bbox[2] - ann.bbox[0],
                    width: 'max-content',
                    height: ann.bbox[3] - ann.bbox[1],
                    color: ann.color,
                    fontSize: `${ann.size}px`,
                    fontFamily: ann.font ? `"${ann.font.replace(/-[a-zA-Z]+$/, '')}", sans-serif` : 'inherit',
                    fontWeight: ann.isBold ? 700 : 400,
                    fontStyle: ann.isItalic ? 'italic' : 'normal',
                    zIndex: 50,
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    background: '#ffffff'
                  }}
                >
                  {ann.richHtml ? (
                    <div 
                      style={{ width: '100%', pointerEvents: 'none' }} 
                      dangerouslySetInnerHTML={{ __html: ann.richHtml }} 
                    />
                  ) : (
                    ann.content
                  )}
                </div>
              );
            }
            if (ann.type === 'text') {
              return (
                <div 
                  key={ann.id} 
                  style={{ 
                    position: 'absolute',
                    left: ann.x,
                    top: ann.y,
                    color: ann.color || '#000',
                    fontSize: `${ann.size}px`,
                    fontFamily: `"${(ann.font || '').replace(/-[a-zA-Z]+$/, '')}", sans-serif`,
                    fontWeight: ann.isBold ? 700 : 400,
                    fontStyle: ann.isItalic ? 'italic' : 'normal',
                    border: activeAnnotationId === ann.id ? '2px solid #2563eb' : '1px dashed transparent',
                    cursor: draggingId === ann.id ? 'grabbing' : 'move',
                    zIndex: 200,
                    padding: '4px',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    borderRadius: '4px',
                    lineHeight: 1,
                    background: activeAnnotationId === ann.id ? 'rgba(37, 99, 235, 0.05)' : 'transparent'
                  }}
                  onMouseDown={(e) => {
                     e.stopPropagation();
                     const rect = e.currentTarget.getBoundingClientRect();
                     setDraggingId(ann.id);
                     setDragOffset({
                       x: e.clientX - rect.left,
                       y: e.clientY - rect.top
                     });
                     setActiveAnnotationId(ann.id);
                     setActiveSpanIndex(null);
                  }}
                >
                  <div 
                    contentEditable 
                    suppressContentEditableWarning
                    onBlur={(e) => {
                      const text = e.target.innerText;
                      setAnnotations(prev => prev.map(a => a.id === ann.id ? { ...a, content: text } : a));
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{ 
                      outline: 'none',
                      minWidth: '20px',
                      minHeight: '20px',
                      lineHeight: 1
                    }}
                  >
                    {ann.content}
                  </div>
                  {activeAnnotationId === ann.id && (
                     <div 
                       style={{ 
                         position: 'absolute', 
                         top: '-12px', 
                         right: '-12px', 
                         background: '#ef4444', 
                         color: 'white', 
                         borderRadius: '50%', 
                         width: '20px', 
                         height: '20px', 
                         display: 'flex', 
                         alignItems: 'center', 
                         justifyContent: 'center', 
                         fontSize: '12px', 
                         cursor: 'pointer',
                         zIndex: 201
                       }}
                       onClick={(e) => {
                         e.stopPropagation();
                         setAnnotations(prev => prev.filter(a => a.id !== ann.id));
                         setActiveAnnotationId(null);
                       }}
                     >
                       ✕
                     </div>
                  )}
                </div>
              );
            }
            return null;
          })}

            {activeTool === 'text' && !isFinished && textSpans.map((span, i) => {
              const rect = span.bbox;
              const replacement = annotations.find(a => a.spanIndex === i && a.page === currentPage);
              const isHovered = hoveredIndex === i;
              
              return (
                <div 
                  key={`${i}-${historyIndex}`}
                  className="hit-zone"
                  data-span-index={i}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    position: 'absolute',
                    left: rect[0],
                    top: rect[1],
                    minWidth: rect[2] - rect[0],
                    width: 'max-content',
                    minHeight: rect[3] - rect[1],
                    height: 'auto',
                    fontSize: `${span.size}px`,
                    background: replacement ? '#ffffff' : 'transparent',
                    boxShadow: 'none',
                    border: isHovered && !replacement && focusedIndex !== i ? '1px solid rgba(37, 99, 235, 0.5)' : '1px solid transparent',
                    borderRadius: '2px',
                    zIndex: replacement ? 100 : (isHovered ? 15 : 10),
                    cursor: 'text',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'background 0.05s',
                  }}

                  onClick={(e) => {
                    e.stopPropagation();
                    const spanEdit = e.currentTarget.querySelector('span[contentEditable]');
                    if (spanEdit) spanEdit.focus();
                  }}
                >
                  <span
                    contentEditable={true}
                    suppressContentEditableWarning={true}
                    spellCheck={false}
                    data-span-edit="true"
                    onMouseUp={saveSelection}
                    onKeyUp={saveSelection}
                    {...(replacement?.richHtml
                      ? { dangerouslySetInnerHTML: { __html: replacement.richHtml } }
                      : { children: replacement ? replacement.content : span.text }
                    )}
                    style={{
                      ...styles.hitInput,
                      fontSize: `${replacement?.size || span.size}px`,
                      background: 'transparent',
                      opacity: 1,
                      color: (replacement || focusedIndex === i) ? (replacement?.color || span.color || '#000') : 'transparent',
                      fontWeight: (replacement?.isBold ?? span.isBold) ? 700 : 400,
                      fontStyle: (replacement?.isItalic ?? span.isItalic) ? 'italic' : 'normal',
                      textDecoration: `${(replacement?.isUnderline ?? span.isUnderline) ? 'underline ' : ''}${(replacement?.isStrikethrough ?? span.isStrikethrough) ? 'line-through' : ''}`.trim() || 'none',
                      textAlign: replacement?.textAlign || span.textAlign || 'left',
                      fontFamily: `"${(replacement?.font || span.font || '').replace(/-[a-zA-Z]+$/, '')}", sans-serif`,
                      display: 'inline-block',
                      minWidth: '100%',
                      whiteSpace: 'nowrap',
                      WebkitFontSmoothing: 'antialiased',
                      MozOsxFontSmoothing: 'grayscale',
                    }}
                    onFocus={(e) => {
                      if (activeSpanIndex !== i) {
                        setActiveSpanIndex(i);
                        setFocusedIndex(i);
                        setHasSelection(false);
                        savedSelectionRef.current = null;
                      }
                      setActiveAnnotationId(null);
                      activeEditRef.current = e.target;
                      
                      e.target.parentElement.style.background = '#ffffff';
                      e.target.parentElement.style.opacity = '1';
                      e.target.parentElement.style.zIndex = '500';
                      e.target.parentElement.style.boxShadow = '0 0 0 2px #fff, 0 0 0 3px #2563eb';
                    }}
                    onBlur={(e) => {
                      // Focus Shield: If we are interacting with the toolbar, do NOT sync state.
                      // Syncing state cause a re-render which destroys the current DOM selection range.
                      if (isToolbarInteractingRef.current || e.relatedTarget?.closest('#formatting-toolbar')) {
                         console.log("[DEBUG] Blur Shield Active: Preventing state sync during toolbar interaction");
                         return;
                      }

                      setFocusedIndex(null);
                      const val = (e.target.innerText || e.target.textContent).replace(/\n|\r/g, "");
                      const richHtml = e.target.innerHTML;
                      // Pass both so they stay in sync
                      handleSpanEdit(i, val.trim() || span.text, (richHtml && richHtml !== val) ? richHtml : null);
                      
                      e.target.parentElement.style.zIndex = replacement ? '100' : '15';
                      if (!replacement && val === span.text) e.target.parentElement.style.background = 'transparent';
                      e.target.parentElement.style.boxShadow = 'none';
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {isFinished && (
          <div style={styles.exportOverlay}>
            <div style={styles.exportPanel}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>
                Export Options ℹ️
              </div>
              <button 
                style={{ width: '100%', height: '52px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', fontSize: '14px', fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', border: 'none', boxShadow: '0 8px 16px -4px rgba(4, 120, 87, 0.4)', cursor: isExporting ? 'wait' : 'pointer', transition: 'all 0.2s', marginBottom: '12px', opacity: isExporting ? 0.7 : 1 }}
                onClick={handleDirectDownload}
                disabled={isExporting}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <span>{isExporting ? 'Generating PDF...' : 'Download as PDF'}</span>
                <span style={{ fontSize: '18px' }}>{isExporting ? '⏳' : '💾'}</span>
              </button>
              
              <button 
                style={{ width: '100%', height: '52px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', fontSize: '14px', fontWeight: 600, color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.2s' }}
                onClick={() => window.location.href = '/'}
                onMouseOver={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
              >
                <span>Go to Main Menu</span>
                <span style={{ fontSize: '18px' }}>🏠</span>
              </button>
              <button 
                style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', marginTop: '24px', textDecoration: 'underline' }}
                onClick={() => setIsFinished(false)}
              >
                Keep Editing
              </button>
            </div>
          </div>
        )}
      </div>
      {!isFinished && (
        <div style={styles.bottomBar}>
          <div style={styles.floatingToolbar}>
            <button style={styles.toolbarBtn} onClick={() => setZoom(prev => Math.max(50, prev - 10))}>➖</button>
            <div style={styles.zoomText}>{zoom}%</div>
            <button style={styles.toolbarBtn} onClick={() => setZoom(prev => Math.min(200, prev + 10))}>➕</button>
            
            <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 8px' }} />
            <button 
              style={styles.finishBtn}
              onClick={() => setIsFinished(true)}
            >
              Finish Editing
            </button>
          </div>

          <div style={styles.paginationBox}>
            <button 
              style={{...styles.toolbarBtn, width: 'auto', padding: '0 12px', fontSize: '13px', fontWeight: 600, opacity: currentPage === 0 ? 0.3 : 1, pointerEvents: currentPage === 0 ? 'none' : 'auto'}} 
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
            >
              Prev Page
            </button>
            <div style={{ ...styles.zoomText, minWidth: '70px', color: '#64748b' }}>Page {currentPage + 1} of {totalPages}</div>
            <button 
              style={{...styles.toolbarBtn, width: 'auto', padding: '0 12px', fontSize: '13px', fontWeight: 600, opacity: currentPage >= totalPages - 1 ? 0.3 : 1, pointerEvents: currentPage >= totalPages - 1 ? 'none' : 'auto'}} 
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
            >
              Next Page
            </button>
          </div>
        </div>
      )}
      {showSignModal && (
        <SignatureModal 
          onCancel={() => setShowSignModal(false)}
          onSave={(img) => {
            setActiveSignature(img);
            setActiveSignatureDims({ w: 180, h: 80 });
            setSavedSignatures(prev => [...prev, img]);
            setShowSignModal(false);
            setActiveSignSubTool('add-sig');
          }}
        />
      )}
    </div>
  );
}

const SignatureModal = ({ onSave, onCancel }) => {
  const [activeTab, setActiveTab] = useState('draw');
  const [color, setColor] = useState('#000000');
  const [typeColor, setTypeColor] = useState('#000000');
  const [thickness, setThickness] = useState(3);
  const [typedText, setTypedText] = useState('');
  const [selectedFont, setSelectedFont] = useState('Pacifico');
  const [error, setError] = useState('');
  
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const fonts = ['Pacifico', 'Dancing Script', 'Great Vibes', 'Satisfy', 'Caveat'];
  const colors = ['#000000', '#0000ff', '#ff0000'];

  useEffect(() => {
    if (activeTab === 'draw' && canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
    }
  }, [activeTab]);

  const startDrawing = (e) => {
    setIsDrawing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = color;
    ctx.lineWidth = thickness;
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
        const reader = new FileReader();
        reader.onload = (ev) => onSave(ev.target.result);
        reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    if (activeTab === 'draw') {
        onSave(canvasRef.current.toDataURL('image/png'));
    } else if (activeTab === 'type') {
        if (!typedText.trim()) {
            setError('Please type your name first');
            return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = 600;
        canvas.height = 200;
        const ctx = canvas.getContext('2d');
        ctx.font = `60px ${selectedFont}`;
        ctx.fillStyle = typeColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedText || 'Signature', 300, 100);
        onSave(canvas.toDataURL('image/png'));
    }
  };

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <div style={styles.modalHeader}>
            <div style={{ background: 'rgba(220, 38, 38, 0.1)', padding: '8px', borderRadius: '8px', color: '#dc2626' }}>
                <PenTool size={20} />
            </div>
            <h3 style={styles.modalTitle}>Add Signature</h3>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096' }} onClick={onCancel}>
                <X size={24} />
            </button>
        </div>

        <div style={styles.modalTabs}>
            <button style={styles.modalTab(activeTab === 'draw')} onClick={() => setActiveTab('draw')}>
                <PenTool size={18} /> Draw
            </button>
            <button style={styles.modalTab(activeTab === 'type')} onClick={() => setActiveTab('type')}>
                <FileText size={18} /> Type
            </button>
            <button style={styles.modalTab(activeTab === 'upload')} onClick={() => setActiveTab('upload')}>
                <RotateCw size={18} /> Upload
            </button>
            <button style={styles.modalTab(activeTab === 'stamps')} onClick={() => setActiveTab('stamps')}>
                <FileText size={18} /> Stamps
            </button>
        </div>

        <div style={{ padding: '0 24px 24px', flex: 1, overflowY: 'auto' }}>
            {activeTab === 'draw' && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #edf2f7', marginTop: '16px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', marginRight: '8px' }}>
                                <Edit3 size={16} />
                                <span style={{ fontSize: '13px', fontWeight: 600 }}>Ink:</span>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                            {colors.map(c => (
                                <div 
                                  key={c} 
                                  style={{ width: '24px', height: '24px', borderRadius: '50%', background: c, cursor: 'pointer', border: color === c ? '2px solid #dc2626' : '2px solid transparent', boxShadow: 'inset 0 0 0 2px #fff' }} 
                                  onClick={() => setColor(c)} 
                                />
                            ))}
                        </div>
                        </div>
                        <button style={{ ...styles.btn(false), padding: '6px 12px', fontSize: '12px' }} onClick={clear}>Clear</button>
                    </div>
                    <div style={{ 
                        background: '#fff', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '12px', 
                        cursor: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>') 0 20, crosshair`, 
                        width: '100%', 
                        height: '300px', 
                        position: 'relative', 
                        overflow: 'hidden' 
                    }}>
                        <canvas 
                            ref={canvasRef}
                            style={{ width: '100%', height: '100%' }}
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={() => setIsDrawing(false)}
                            onMouseOut={() => setIsDrawing(false)}
                        />
                    </div>
                </>
            )}

            {activeTab === 'type' && (
                <>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#4a5568', marginBottom: '8px', marginTop: '20px' }}>Type your name</div>
                    <input 
                        type="text" 
                        placeholder="Type your name..." 
                        style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '16px', outline: 'none' }}
                        value={typedText}
                        onChange={(e) => setTypedText(e.target.value)}
                    />

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px', background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #edf2f7' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', marginRight: '8px' }}>
                            <Edit3 size={16} />
                            <span style={{ fontSize: '13px', fontWeight: 600 }}>Ink:</span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {colors.map(c => (
                                <div 
                                  key={c} 
                                  style={{ width: '24px', height: '24px', borderRadius: '50%', background: c, cursor: 'pointer', border: typeColor === c ? '2px solid #059669' : '2px solid transparent', boxShadow: 'inset 0 0 0 2px #fff' }} 
                                  onClick={() => setTypeColor(c)} 
                                />
                            ))}
                        </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
                        {fonts.map(f => (
                            <div 
                              key={f} 
                              style={{ padding: '24px', borderRadius: '12px', border: selectedFont === f ? '2px solid #059669' : '1px solid #e2e8f0', background: '#fff', fontFamily: f, fontSize: '24px', textAlign: 'center', cursor: 'pointer', color: typeColor }} 
                              onClick={() => setSelectedFont(f)}
                            >
                                {typedText || 'Signature'}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {activeTab === 'upload' && (
                <div style={{ height: '300px', border: '2px dashed #e2e8f0', borderRadius: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', background: '#f8fafc', marginTop: '20px' }}>
                    <div style={{ background: '#fff', padding: '16px', borderRadius: '50%', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <Edit3 size={40} color="#dc2626" />
                    </div>
                    <input type="file" accept="image/*" onChange={handleFileUpload} style={{ display: 'none' }} id="sig-upload-editor" />
                    <button style={styles.btn(true)} onClick={() => document.getElementById('sig-upload-editor').click()}>Choose File</button>
                </div>
            )}

            {activeTab === 'stamps' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px', marginTop: '20px' }}>
                    {['APPROVED', 'REJECTED', 'DRAFT', 'SIGN HERE', 'CONFIDENTIAL'].map(text => (
                        <div 
                          key={text} 
                          style={{ padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }} 
                          onClick={() => {
                            const canvas = document.createElement('canvas');
                            canvas.width = 200;
                            canvas.height = 80;
                            const ctx = canvas.getContext('2d');
                            ctx.strokeStyle = text === 'REJECTED' ? '#ef4444' : '#2563eb';
                            ctx.lineWidth = 4;
                            ctx.strokeRect(5, 5, 190, 70);
                            ctx.font = 'bold 24px Arial';
                            ctx.fillStyle = text === 'REJECTED' ? '#ef4444' : '#2563eb';
                            ctx.textAlign = 'center';
                            ctx.textBaseline = 'middle';
                            ctx.fillText(text, 100, 40);
                            onSave(canvas.toDataURL('image/png'));
                        }}>
                            <div style={{ padding: '8px', border: '2px solid #e2e8f0', borderRadius: '4px', color: text === 'REJECTED' ? '#ef4444' : '#2563eb', fontWeight: 'bold', fontSize: '12px' }}>{text}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>

        <div style={styles.modalFooter}>
            <button style={styles.btn(false)} onClick={onCancel}>Cancel</button>
            <button style={styles.btn(true)} onClick={handleSave}>
                <Check size={18} /> Add to PDF
            </button>
        </div>
      </div>
    </div>
  );
};

const DraggableSignature = ({ sig, dims, onUpdate, onRemove, isSelected, onSelect, zoom, innerPageRef }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [isRotating, setIsRotating] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [activeHandle, setActiveHandle] = useState(null);

    const handleMouseDown = (e, type, handle = null) => {
        e.stopPropagation();
        onSelect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        setStartPos({ x: clientX, y: clientY });
        if (type === 'drag') setIsDragging(true);
        if (type === 'resize') {
            setIsResizing(true);
            setActiveHandle(handle);
        }
        if (type === 'rotate') setIsRotating(true);
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging && !isResizing && !isRotating) return;
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            const dx = clientX - startPos.x;
            const dy = clientY - startPos.y;

            if (isDragging) {
                onUpdate({ ...sig, x: sig.x + dx, y: sig.y + dy });
            } else if (isResizing) {
                let { x, y, w, h } = sig;
                const ratio = sig.w / sig.h;
                const useShift = e.shiftKey;

                if (activeHandle === 'br') {
                    w = Math.max(20, w + dx);
                    if (useShift) { h = w / ratio; } else { h = Math.max(10, h + dy); }
                } else if (activeHandle === 'bl') {
                    const newW = Math.max(20, w - dx);
                    if (newW > 20) { 
                        w = newW;
                        if (useShift) { h = w / ratio; x = sig.x + (sig.w - w); } else { x += dx; h = Math.max(10, h + dy); }
                    }
                } else if (activeHandle === 'tr') {
                    w = Math.max(20, w + dx);
                    const newH = Math.max(10, h - dy);
                    if (newH > 10) {
                        h = newH;
                        if (useShift) { w = h * ratio; y = sig.y + (sig.h - h); } else { y += dy; }
                    }
                } else if (activeHandle === 'tl') {
                    const newW = Math.max(20, w - dx);
                    const newH = Math.max(10, h - dy);
                    if (useShift) {
                        if (Math.abs(dx) > Math.abs(dy)) { w = newW; h = w / ratio; } else { h = newH; w = h * ratio; }
                        x = sig.x + (sig.w - w); y = sig.y + (sig.h - h);
                    } else {
                        if (newW > 20) { x += dx; w = newW; }
                        if (newH > 10) { y += dy; h = newH; }
                    }
                }
                onUpdate({ ...sig, x, y, w, h });
            } else if (isRotating && innerPageRef.current) {
                const pageRect = innerPageRef.current.getBoundingClientRect();
                const scale = zoom / 100;
                
                // Calculate center in screen coordinates
                const centerX = pageRect.left + (sig.x + sig.w / 2) * scale;
                const centerY = pageRect.top + (sig.y + sig.h / 2) * scale;
                
                const angle = Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);
                onUpdate({ ...sig, rotation: angle + 90 });
            }
            setStartPos({ x: clientX, y: clientY });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            setIsResizing(false);
            setIsRotating(false);
            setActiveHandle(null);
        };

        if (isDragging || isResizing || isRotating) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleMouseMove, { passive: false });
            window.addEventListener('touchend', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleMouseMove);
            window.removeEventListener('touchend', handleMouseUp);
        };
    }, [isDragging, isResizing, isRotating, sig, startPos, activeHandle, onUpdate]);

    return (
        <div 
            id={`sig-${sig.id}`}
            style={{
                ...styles.signatureOverlay(isSelected),
                left: sig.x,
                top: sig.y,
                width: sig.w,
                height: sig.h,
                transform: `rotate(${sig.rotation || 0}deg)`,
            }}
            onMouseDown={(e) => handleMouseDown(e, 'drag')}
        >
            <img src={sig.image} style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} alt="signature" />
            {isSelected && (
                <>
                    {/* Corner Handles */}
                    <div style={{ position: 'absolute', width: '12px', height: '12px', background: '#059669', border: '2px solid #fff', borderRadius: '50%', left: '-6px', top: '-6px', cursor: 'nwse-resize', zIndex: 120, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'tl')} />
                    <div style={{ position: 'absolute', width: '12px', height: '12px', background: '#059669', border: '2px solid #fff', borderRadius: '50%', right: '-6px', top: '-6px', cursor: 'nesw-resize', zIndex: 120, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'tr')} />
                    <div style={{ position: 'absolute', width: '12px', height: '12px', background: '#059669', border: '2px solid #fff', borderRadius: '50%', left: '-6px', bottom: '-6px', cursor: 'nesw-resize', zIndex: 120, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'bl')} />
                    <div style={{ position: 'absolute', width: '12px', height: '12px', background: '#059669', border: '2px solid #fff', borderRadius: '50%', right: '-6px', bottom: '-6px', cursor: 'nwse-resize', zIndex: 120, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'br')} />

                    {/* Rotation Handle - Top Center */}
                    <div 
                        style={{ position: 'absolute', top: '-40px', left: '50%', transform: 'translateX(-50%)', width: '32px', height: '32px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab', boxShadow: '0 4px 10px rgba(0,0,0,0.1)', zIndex: 120 }} 
                        onMouseDown={(e) => handleMouseDown(e, 'rotate')}
                    >
                        <RotateCw size={16} color="#1e293b" />
                    </div>

                    {/* Trash Handle - Bottom Center */}
                    <div 
                      style={{ position: 'absolute', bottom: '-45px', left: '50%', transform: 'translateX(-50%)', width: '36px', height: '36px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.1)', zIndex: 120, color: '#1e293b' }}
                      onClick={(e) => { e.stopPropagation(); onRemove(sig.id); }}
                    >
                      <Trash2 size={18} />
                    </div>
                </>
            )}
        </div>
    );
};
